import React, { useState, useMemo, useCallback, useEffect } from "react";
import { graphql, useMutation, useFragment } from "react-relay";
import {
  AHButton,
  Box,
  ScaleFactorProvider,
  SectionHeader,
  useLayoutContext,
  useScaleFactor,
} from "arkh";
import { ScrollView } from "react-native-gesture-handler";
import { FormattedMessage } from "react-intl";
import { WidgetLayoutProvider } from "widgets";
import { WidgetNotePage } from "widgets/src/WidgetNote/WidgetNotePage";
import { WidgetSettingsHeader } from "../../WidgetSettings/WidgetSettingsHeader";
import { useGetWidgetTypeTitle } from "../../WidgetSettings/useGetWidgetTypeTitle";
import { WidgetSettingsTitleInput } from "../../common/WidgetSettingsTitleInput";
import { ReorderableList } from "../../../../../components/mobile/ReoardrableList/ReordrableList";

import { Positions } from "../../../../../components/Positions";
import { NoteSettingsUpdateMutation } from "./__generated__/NoteSettingsUpdateMutation.graphql";
import { NoteSettings_Note$key } from "./__generated__/NoteSettings_Note.graphql";
import { useIntlTokens } from "../../../../../components/mobile/useIntlTokens";
import { NoteSettingsDeleteMutation } from "./__generated__/NoteSettingsDeleteMutation.graphql";
import { useAlert } from "../../../../../common/Alert";

export function NoteSettings(props: {
  note: NoteSettings_Note$key;
  close: () => void;
  enableDelete?: boolean;
}) {
  const note = useFragment(
    graphql`
      fragment NoteSettings_Note on Note {
        id
        title
        noteSize
        pages {
          id

          includedInSlideshow
          ...WidgetNotePage_widgetNotePage
        }
      }
    `,
    props.note
  );

  const getWidgetTypeTitle = useGetWidgetTypeTitle();
  const headerTitle = getWidgetTypeTitle("WidgetNote");

  const intl_tokens = useIntlTokens();
  const layout = useLayoutContext();
  const remoteOrderKey = useMemo(
    () =>
      note.pages
        .map(({ id, includedInSlideshow }) => id + includedInSlideshow)
        .join(""),
    [note.pages]
  );
  type Page = (typeof note.pages)[number];
  const [localPages, setLocalPages] = useState<Page[]>(note.pages.slice());
  const orderKey = useMemo(
    () =>
      localPages
        .map(({ id, includedInSlideshow }) => id + includedInSlideshow)
        .join(""),
    [localPages]
  );
  const [localTitle, setLocalTitle] = useState<string | null>(null);

  useEffect(() => {
    setLocalTitle(note.title);
  }, [note.title]);

  useEffect(() => {
    setLocalPages(note.pages.slice());
  }, [note.pages]);
  const dirty = useMemo(
    () => note.title !== localTitle || remoteOrderKey !== orderKey,
    [note.title, localTitle, remoteOrderKey, orderKey]
  );

  const [_update, loading] = useMutation<NoteSettingsUpdateMutation>(graphql`
    mutation NoteSettingsUpdateMutation(
      $noteId: ID!
      $setTitle: String
      $setPageIds: [ID!]
    ) {
      note(id: $noteId) {
        update(update: { setTitle: $setTitle, setPageIds: $setPageIds }) {
          id
          ...NoteSettings_Note
        }
      }
    }
  `);

  const update = useCallback(() => {
    _update({
      onCompleted: props.close,
      variables: {
        noteId: note.id,
        setPageIds: localPages.map(({ id }) => id),
        setTitle: localTitle,
      },
    });
  }, [_update, localPages, localTitle, note.id, props.close]);

  const size = {
    w: 2,
    h: note.noteSize === "TWO_X_TWO" ? 2 : 1,
  };

  const unitSize = 128;
  const pageWidth = useMemo(() => {
    return size.w * unitSize;
  }, [size.w]);
  const pageHeight = useMemo(() => {
    return size.h * unitSize;
  }, [size.h]);
  const sf = useScaleFactor();
  const sdf = useMemo(() => sf * 0.4, [sf]);

  const [deleteNote] = useMutation<NoteSettingsDeleteMutation>(graphql`
    mutation NoteSettingsDeleteMutation($noteId: ID!) {
      note(id: $noteId) {
        delete {
          id @deleteRecord
        }
      }
    }
  `);
  const { alert } = useAlert();
  return (
    <Box style={{ flex: 1 }} color="surface-background-light">
      <WidgetSettingsHeader
        dirty={dirty}
        title={headerTitle}
        loading={loading}
        onClose={props.close}
        onDone={update}
      />
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{
          paddingVertical: layout.space.medium,
        }}
      >
        <Box spaceHorizontal="medium">
          <WidgetSettingsTitleInput
            title={localTitle ?? undefined}
            setTitle={setLocalTitle}
          />
          <Box spaceBottom="large" />
          <SectionHeader>
            <SectionHeader.Title>
              <FormattedMessage
                defaultMessage="Pages"
                description="Widget note pages title"
              />
            </SectionHeader.Title>
          </SectionHeader>
          <ReorderableList
            items={localPages}
            itemHeight={pageHeight * sdf + layout.space.large * 2}
            keyExtractor={({ id }) => id}
            onReorder={(order) => {
              setLocalPages(orderToPages(order, localPages));
            }}
            renderItem={(page) => (
              <Box
                spaceHorizontal="medium"
                style={{
                  justifyContent: "flex-start",
                  flexDirection: "row",
                  alignItems: "center",
                  alignContent: "center",
                  flex: 1,
                }}
                color="surface-backgroundControl"
              >
                <ScaleFactorProvider value={(unitSize * sdf) / 236}>
                  <WidgetLayoutProvider
                    value={{
                      unitSize: unitSize * sdf,
                      height: pageHeight * sdf,
                      width: pageWidth * sdf,
                      h: size.h,
                    }}
                  >
                    <Box
                      style={{
                        height: pageHeight * sdf,
                        width: pageWidth * sdf,
                        overflow: "hidden",
                      }}
                      cornerRadius="large"
                    >
                      <WidgetNotePage page={page} />
                    </Box>
                  </WidgetLayoutProvider>
                </ScaleFactorProvider>
              </Box>
            )}
          />
        </Box>
      </ScrollView>
      {props.enableDelete ? (
        <Box space="large">
          <AHButton
            themeType="negative"
            title={intl_tokens.Delete}
            onPress={() => {
              alert("Delete Note", "Are you sure?", [
                {
                  style: "cancel",
                },
                {
                  style: "destructive",
                  text: intl_tokens.Delete,
                  onPress() {
                    deleteNote({
                      variables: { noteId: note.id },
                      onCompleted: props.close,
                    });
                  },
                },
              ]);
            }}
          />
        </Box>
      ) : null}
    </Box>
  );
}

function orderToPages<T extends { readonly id: string }>(
  order: Positions,
  pages: T[]
) {
  return Object.keys(order)
    .sort((a, b) => order[a] - order[b])
    .flatMap((key) => {
      const page = pages.find(({ id }) => id === key);
      return page ? [page] : [];
    });
}
